import React, { useState } from "react";
import { Copy, Globe, Loader2, RefreshCw, ShieldCheck, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { errorMessage, formsApi } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { cn } from "../../lib/utils";

const inputCls = "h-10 rounded-none border-border bg-transparent px-3 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";

const STATUS = {
  verified: { label: "Verified", cls: "border-emerald-500/50 text-emerald-400" },
  pending: { label: "Checking DNS…", cls: "border-amber-500/50 text-amber-400" },
  not_started: { label: "Waiting for DNS records", cls: "border-border text-muted-foreground" },
  failed: { label: "Verification failed", cls: "border-destructive/60 text-destructive" },
  temporary_failure: { label: "Retrying…", cls: "border-amber-500/50 text-amber-400" },
  error: { label: "Could not reach Resend", cls: "border-destructive/60 text-destructive" },
};

const StatusPill = ({ status }) => {
  const s = STATUS[status] || { label: status, cls: "border-border text-muted-foreground" };
  return (
    <span className={cn("border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em]", s.cls)} data-testid="domain-status">
      {s.label}
    </span>
  );
};

const Mono = ({ value, testId }) => (
  <button type="button" onClick={() => copyToClipboard(value, "Copied")} className="group flex w-full items-start gap-2 text-left" title="Click to copy" data-testid={testId}>
    <code className="min-w-0 flex-1 break-all font-mono text-xs leading-relaxed text-foreground/90">{value}</code>
    <Copy className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-primary" />
  </button>
);

const RecordsTable = ({ records }) => (
  <div className="overflow-hidden border border-border" data-testid="dns-records">
    <div className="hidden grid-cols-[70px_1fr_2fr_60px] gap-3 border-b border-border bg-background/60 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground md:grid">
      <span>Type</span>
      <span>Host / Name</span>
      <span>Value</span>
      <span>Priority</span>
    </div>
    {records.map((r, i) => (
      <div key={`${r.type}-${r.name}-${i}`} className="grid gap-2 border-b border-border px-4 py-3 last:border-b-0 md:grid-cols-[70px_1fr_2fr_60px] md:gap-3" data-testid={`dns-record-${i}`}>
        <span className="text-xs font-semibold">
          {r.type} <span className="ml-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{r.record}</span>
        </span>
        <Mono value={r.name} testId={`dns-name-${i}`} />
        <Mono value={r.value} testId={`dns-value-${i}`} />
        <span className="text-xs text-muted-foreground">{r.priority ?? "—"}</span>
      </div>
    ))}
  </div>
);

const AddDomain = ({ onAdded }) => {
  const [name, setName] = useState("mail.avalontattoocollective.ca");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try {
      onAdded(await formsApi.addDomain(name.trim().toLowerCase()));
      toast.success("Domain added to Resend", { description: "Now add the DNS records below." });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end" data-testid="add-domain-form">
      <div className="space-y-1.5">
        <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Sending domain</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="add-domain-name" />
      </div>
      <Button onClick={add} disabled={busy || !name.trim()} className="h-10 rounded-none px-5 text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid="add-domain-submit">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Add domain
      </Button>
    </div>
  );
};

const SenderForm = ({ status, onSaved }) => {
  const domain = status.domain?.name || "";
  const verified = status.domain?.status === "verified";
  const [name, setName] = useState(status.sender_name || "Avalon Tattoo Collective");
  const [local, setLocal] = useState((status.sender_email || "hello@").split("@")[0] || "hello");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const res = await formsApi.saveSender(name, `${local}@${domain}`);
      toast.success("From address updated", { description: res.sender });
      onSaved();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 border border-border bg-background/40 p-4" data-testid="sender-form">
      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-primary">From address</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Sender name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="sender-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
          <div className="flex items-center">
            <Input value={local} onChange={(e) => setLocal(e.target.value.replace(/[^a-z0-9._+-]/gi, ""))} className={cn(inputCls, "rounded-r-none")} data-testid="sender-local" />
            <span className="flex h-10 items-center border border-l-0 border-border px-3 text-xs text-muted-foreground">@{domain}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Currently sending as <span className="text-foreground">{status.sender}</span>
          {status.sandbox && " (Resend sandbox — delivers only to your own address)"}
        </p>
        <Button onClick={save} disabled={busy || !verified || !local} className="h-10 rounded-none px-5 text-[11px] font-semibold uppercase tracking-[0.2em]" title={verified ? "" : "Verify the domain first"} data-testid="sender-save">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />} Use this address
        </Button>
      </div>
    </div>
  );
};

/** Add a sending domain in Resend, show the DNS records to paste into Wix, verify, then pick the From address. */
const SendingDomainCard = ({ status, onRefresh }) => {
  const [verifying, setVerifying] = useState(false);
  if (!status?.configured) return null;
  const domain = status.domain;

  const verify = async () => {
    setVerifying(true);
    try {
      const d = await formsApi.verifyDomain();
      toast[d.status === "verified" ? "success" : "info"](d.status === "verified" ? "Domain verified" : `Status: ${STATUS[d.status]?.label || d.status}`, {
        description: d.status === "verified" ? "You can now pick your From address below." : "DNS changes can take up to an hour to propagate. Try again shortly.",
      });
      onRefresh();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <section className="border border-border bg-card" data-testid="sending-domain-card">
      <header className="flex flex-col gap-3 border-b border-border px-6 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
          <Globe className="h-4 w-4 text-primary" /> Sending domain {domain?.name && <span className="break-all text-muted-foreground">· {domain.name}</span>}
        </div>
        {domain && (
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill status={domain.status} />
            <Button size="sm" variant="outline" onClick={verify} disabled={verifying} className="h-9 max-w-full whitespace-normal rounded-none text-left text-[10px] font-semibold uppercase tracking-[0.18em]" data-testid="domain-verify">
              {verifying ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-2 h-3.5 w-3.5" />} {domain.status === "verified" ? "Re-check" : "I've added the records — verify"}
            </Button>
          </div>
        )}
      </header>

      <div className="space-y-5 px-6 py-5">
        {!domain && (
          <>
            <p className="text-sm text-muted-foreground">
              Auto-replies can only reach customers from an address on a domain you own. Add a sending domain here and Resend will give you the DNS records to paste into Wix.
            </p>
            <AddDomain onAdded={onRefresh} />
          </>
        )}

        {domain && domain.status !== "verified" && (
          <>
            {domain.error && <p className="text-sm text-destructive">{domain.error}</p>}
            <ol className="list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground" data-testid="wix-steps">
              <li>
                In Wix go to <span className="text-foreground">Settings → Domains → ⋯ next to avalontattoocollective.ca → Manage DNS Records</span>.
              </li>
              <li>
                For each row below click <span className="text-foreground">+ Add Record</span>, choose the <span className="text-foreground">Type</span>, paste the <span className="text-foreground">Host/Name</span> and <span className="text-foreground">Value</span> exactly (click any value here to copy it). For the MX record also enter the priority.
              </li>
              <li>Save, wait a few minutes (Wix can take up to an hour), then click "I've added the records — verify" above.</li>
            </ol>
            <RecordsTable records={domain.records || []} />
          </>
        )}

        {domain && domain.status === "verified" && <p className="text-sm text-emerald-400">Domain verified — pick the address customers will see below.</p>}

        {domain && <SenderForm key={`${domain.status}-${status.sender_email}`} status={status} onSaved={onRefresh} />}
      </div>
    </section>
  );
};

export default SendingDomainCard;
